import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Title from '../../components/common/Title';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import Icon from '../../components/common/Icon';
import Loading from '../../components/common/Loading';
import { apiRequest } from '../../utils';
import { clearCart } from './cart';

const ShopConfirmation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(null);

  // Two ways in: (1) straight from checkout (no online payment) — the result is
  // in sessionStorage; (2) back from Stripe as ?order=<id> (usePersistUrlParams
  // stashes it in sessionStorage) — fetch the status and poll until the webhook
  // records it.
  const returnOrderId = new URLSearchParams(location.search).get('order')
    || sessionStorage.getItem('order');
  const [phase, setPhase] = useState(returnOrderId ? 'polling' : 'stored');
  const [payingAgain, setPayingAgain] = useState(false);
  const polls = useRef(0);

  useEffect(() => {
    if (returnOrderId) return undefined;
    try {
      const raw = sessionStorage.getItem('shopLastOrder');
      if (raw) setOrder(JSON.parse(raw));
    } catch { /* ignore */ }
    return undefined;
  }, [returnOrderId]);

  useEffect(() => {
    if (!returnOrderId) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const d = await apiRequest('GET', `/storefront/order/${returnOrderId}`);
        if (cancelled) return;
        setOrder(d);
        if (d.paid) {
          setPhase('paid');
          clearCart();
          sessionStorage.removeItem('order');
          return;
        }
        polls.current += 1;
        setPhase(polls.current >= 6 ? 'processing' : 'polling');
        if (polls.current < 6) timer = setTimeout(tick, 2500);
      } catch {
        if (!cancelled) setPhase('stored');
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [returnOrderId]);

  const payAgain = async () => {
    setPayingAgain(true);
    try {
      const d = await apiRequest('POST', '/storefront/pay-again', { order_id: returnOrderId });
      if (d.checkout_url) window.location.href = d.checkout_url;
      else setPayingAgain(false);
    } catch { setPayingAgain(false); }
  };

  if ((phase === 'polling' || phase === 'processing') && !(order && order.paid)) {
    return (
      <Base activeNav="shop">
        <Title>{t('paymentProcessing', { defaultValue: 'Finishing your payment…' })}</Title>
        <Card className="text-center">
          {phase === 'processing' ? (
            <>
              <p className="mt-2 mb-3">{t('paymentProcessingEmail', {
                defaultValue: "Your payment is being processed — we'll email your confirmation and invoice as soon as it clears." })}</p>
              <Button theme="primary" onClick={payAgain} disabled={payingAgain}>
                {t('payNow', { defaultValue: 'Pay now' })}
              </Button>
            </>
          ) : (
            <>
              <Loading size="lg" />
              <p className="mt-3 mb-0 text-muted">{t('paymentProcessingBody', {
                defaultValue: "We're confirming your payment — this only takes a moment." })}</p>
            </>
          )}
        </Card>
      </Base>
    );
  }

  // No order in the session (e.g. landed here directly) — back to the shop.
  if (!order) {
    return (
      <Base activeNav="shop">
        <Title>{t('shop')}</Title>
        <Card className="text-center">
          <Button to="/shop" theme="primary">{t('shop')}</Button>
        </Card>
      </Base>
    );
  }

  return (
    <Base activeNav="shop">
      <Title>
        <Icon name="check" theme="primary" className="me-2" style={{ fontSize: '1.4rem' }} />
        {t('orderPlaced')}
      </Title>

      <Card className="text-center">
        <Icon name="check-circle" theme="primary" style={{ fontSize: '2.4rem' }} />
        {order.invoice_number && (
          <p className="mt-3 mb-1">
            {order.is_proposal ? t('proposalNumber') : t('invoiceNumber')}: <strong>{order.invoice_number}</strong>
          </p>
        )}
        <p className="mt-2 mb-0 text-muted">
          {order.paid
            ? t('confirmationMailWithInvoice', { defaultValue: 'Your confirmation and invoice are on their way by email.' })
            : t('invoiceToFollow')}
        </p>
        <Button className="mt-3" theme="primary" onClick={() => navigate('/home')}>
          {t('backToHome')}
        </Button>
        <Button className="mt-2" theme="light" onClick={() => navigate('/shop')}>
          {t('shop')}
        </Button>
      </Card>
    </Base>
  );
};

export default ShopConfirmation;
